import { secondsSince } from './clock'
import { t, type StaffLanguage } from './staffText'
import type { DeclinedView, DeskStatus, Permission, RequestKind, RequestView } from './types'

/**
 * A desk's open request, read for the Counter page.
 *
 * Every rule here mirrors org.DeriveStatus and the four kinds the engine knows
 * (internal/org/deskstatus.go): what the operator sees is never the desk's stored
 * open/paused/closed state alone, it is that state read together with the
 * request riding alongside the pause. Pulled out of pages/Counter.tsx because the
 * web test suite runs in Node with no DOM — a clock read wrong here is a clerk
 * told they are four minutes into a break they only just started, and nothing
 * short of running the page would have caught it.
 */

/** m:ss, or h:mm:ss past the hour: a clock read at a glance. */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const ss = String(s % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`
}

/** The kind as the operator reads it: on the Ask menu, and wherever a status or a
 *  decline names it. "close" reuses counter.closeDesk — asking to close the desk
 *  and closing it are the same words to a reader, whichever is in front of them. */
export function requestKindLabel(kind: RequestKind, lang: StaffLanguage): string {
  switch (kind) {
    case 'lunch':
      return t('counter.kind.lunch', lang)
    case 'short_break':
      return t('counter.kind.short_break', lang)
    case 'task':
      return t('counter.kind.task', lang)
    case 'close':
      return t('counter.closeDesk', lang)
  }
}

/** The big word for a paused desk: the status itself for Pending, and the kind
 *  for a running break. '' for every status this has nothing to say about —
 *  Ready, Serving and Closed are the free area's and the stage's to draw. */
export function pausedHeadline(status: DeskStatus, lang: StaffLanguage): string {
  switch (status) {
    case 'pending':
      return t('counter.pending', lang)
    case 'lunch':
    case 'short_break':
    case 'task':
      return requestKindLabel(status, lang)
    case 'break':
      // A pause from before requests existed, or one left by a path that pauses
      // without one — the Breaks report's own name for it (deskrequests.go).
      return t('counter.breakEarlier', lang)
    default:
      return ''
  }
}

/** The detail under the big word: how long Pending has waited, how long a
 *  running break has taken (m:ss, never a frozen "0 min"), or — for a short break with a set length — how long
 *  is left or how far over. `late` is set exactly when that is over: the stage
 *  this detail sits in reads it for the same red it gives a service-level
 *  breach.
 *
 *  `request` absent (status 'break') draws nothing: that status has no request
 *  to read a kind or a clock from, and the free area keeps drawing its own
 *  clock from pausedSince, as it always did. */
export function pausedDetail(
  status: DeskStatus,
  request: RequestView | undefined,
  now: number,
  lang: StaffLanguage,
): { text: string; late: boolean } {
  if (!request) return { text: '', late: false }
  switch (status) {
    case 'pending':
      return {
        text: t('counter.pendingWhat', lang, {
          kind: requestKindLabel(request.kind, lang),
          time: clock(secondsSince(request.requestedAt, now)),
        }),
        late: false,
      }
    case 'lunch':
    case 'short_break':
    case 'task':
      // Every break runs until Back, with no length of its own: a running clock.
      return { text: clock(secondsSince(request.startedAt ?? request.requestedAt, now)), late: false }
    default:
      return { text: '', late: false }
  }
}

/** How long a decline stays on the free desk's card. */
export const DECLINED_SHOWN_MIN = 10

/** Whether the free desk still shows a decline. The server sends it until the
 *  operator asks again; the card drops it sooner: once a customer has come to the
 *  desk since (`actedOn`, the decidedAt then held) or DECLINED_SHOWN_MIN after it. */
export function declinedShown(declined: DeclinedView | undefined, now: number, actedOn?: string): boolean {
  if (!declined) return false
  if (!declined.decidedAt) return true
  if (declined.decidedAt === actedOn) return false
  const at = Date.parse(declined.decidedAt)
  return Number.isNaN(at) || now - at < DECLINED_SHOWN_MIN * 60_000
}

/** The one line Declined shows: the kind and, when the manager left one, their reason. */
export function declinedLine(declined: DeclinedView, lang: StaffLanguage): string {
  const kind = requestKindLabel(declined.kind, lang)
  return declined.reason
    ? t('counter.declinedWithReason', lang, { kind, reason: declined.reason })
    : t('counter.declinedNoReason', lang, { kind })
}

/** Whether the waiting list may offer this ticket its own out-of-turn Call
 *  button and reason form.
 *
 *  Mirrors the server's CallSpecific rule (internal/engine, VIP call): calling
 *  the ticket Call next would choose needs no permission, and neither does a
 *  ticket parked in this desk's or this person's own pool — but those never
 *  reach this list; a pool is drawn separately. Everything else here is out of
 *  turn, and without ticket.vip it would answer 403 VIP_CALL_REQUIRED. Offering
 *  the button anyway and letting the server refuse it would teach an operator
 *  that the buttons on this screen cannot be trusted — the same reasoning every
 *  other verb on it is offered by. */
export function offersOutOfTurnCall(ticketId: string, nextId: string | undefined, permissions: readonly Permission[]): boolean {
  return ticketId === nextId || permissions.includes('ticket.vip')
}
